type GridRect = { x: number; y: number; w: number; h: number };

export type GridWidgetPlacement = Pick<GridRect, 'x' | 'y' | 'w' | 'h'>;

const rectsOverlap = (a: GridRect, b: GridRect): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const toGridRect = (widget: GridWidgetPlacement): GridRect => ({
  x: widget.x ?? 0,
  y: widget.y ?? 0,
  w: widget.w ?? 1,
  h: widget.h ?? 1,
});

type FindFirstAvailableGridPositionParams = {
  existingWidgets: GridWidgetPlacement[];
  w: number;
  h: number;
  numColumns: number;
};

/**
 * Finds the top-left first non-overlapping cell for a widget of size (w × h).
 * Scans in grid reading order (row by row, then column).
 */
export const findFirstAvailableGridPosition = ({
  existingWidgets,
  w,
  h,
  numColumns,
}: FindFirstAvailableGridPositionParams): { x: number; y: number } => {
  const columns = Math.max(numColumns, 1);
  const placed = existingWidgets.map(toGridRect);
  const maxY = placed.reduce((max, rect) => Math.max(max, rect.y + rect.h), 0);
  const maxX = columns - w;

  // Widget is wider than the available grid columns — it cannot be placed without
  // overflowing. Fall back to the first column below all existing widgets so the
  // widget at least doesn't collide, even if GridStack will need to clamp its width.
  if (maxX < 0) {
    return { x: 0, y: maxY };
  }

  for (let y = 0; y <= maxY + h; y++) {
    for (let x = 0; x <= maxX; x++) {
      const candidate: GridRect = { x, y, w, h };
      if (!placed.some(rect => rectsOverlap(candidate, rect))) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: maxY };
};
